import React, { useState, useEffect, useRef } from 'react'
import styles from './ConversationalMode.module.scss'
import { Message } from '../utils/conversationStorage'
import type { DesktopSuggestion } from '../dashboardActionsConfig.tsx'

interface MessageBubbleProps {
  message: Message
  suggestion?: DesktopSuggestion | null
  onSuggestionAction?: () => void
  isLatest?: boolean
  onTypingStateChange?: (messageId: string, isTyping: boolean) => void
}

const useTypewriter = (text: string, enabled: boolean, speed: number = 12) => {
  const [displayedText, setDisplayedText] = useState(enabled ? '' : text)
  const [isTyping, setIsTyping] = useState(enabled)
  const hasTyped = useRef(false)

  useEffect(() => {
    if (!enabled || hasTyped.current) {
      setDisplayedText(text)
      setIsTyping(false)
      return
    }

    setDisplayedText('')
    setIsTyping(true)
    let i = 0
    
    const interval = setInterval(() => {
      i++
      setDisplayedText(text.slice(0, i))
      if (i >= text.length) {
        clearInterval(interval)
        setIsTyping(false)
        hasTyped.current = true
      }
    }, speed)

    return () => clearInterval(interval)
  }, [text, enabled, speed])

  return { displayedText, isTyping }
}


// Detecta si el mensaje contiene pasos numerados
const isGuideMessage = (content: string): boolean => {
  return content.includes('Paso 1:') || content.includes('Paso 1 :')
}

// Palabras/frases clave para resaltar (ordenadas de más específicas a menos específicas)
const HIGHLIGHT_TERMS = [
  'profesionales verificados',
  'perfil de Edify',
  'Edify',
  'publicar una obra',
  'publicar tu obra',
  'obras disponibles',
  'ofertas de trabajo',
  'oferta de trabajo',
  'servicio/producto',
  'cotizaciones',
  'presupuestos',
  'propuestas',
  'documentación',
  'oportunidades',
  'tu perfil',
  'servicios',
  'servicio',
  'obras',
  'obra',
  'producto',
  'materiales',
  'comparar',
  'explorar',
  'sección'
]

// Detecta si el texto tiene formato de lista (viñetas o items)
const hasListFormat = (text: string): boolean => {
  return text.includes('•') || text.includes('\n•') || text.includes('\n-')
}

// Formatea el texto del mensaje para mejor legibilidad
const formatMessageText = (text: string, messageId: string): React.ReactNode[] => {
  let highlightIndex = 0
  
  const applyMarkdownBold = (str: string): React.ReactNode[] => {
    const boldRegex = /\*\*([^*]+)\*\*/g
    const parts: React.ReactNode[] = []
    let lastIndex = 0
    let match
    
    while ((match = boldRegex.exec(str)) !== null) {
      if (match.index > lastIndex) {
        parts.push(str.substring(lastIndex, match.index))
      }
      highlightIndex++
      parts.push(
        <span key={`${messageId}-b-${highlightIndex}`} className={styles.boldText}>
          {match[1]}
        </span>
      )
      lastIndex = match.index + match[0].length
    }
    
    if (lastIndex < str.length) {
      parts.push(str.substring(lastIndex))
    }
    
    return parts.length > 0 ? parts : [str]
  }
  
  const truncateUrl = (url: string, maxLength: number = 50): string => {
    if (url.length <= maxLength) return url
    
    const parts = url.split('/')
    const lastPart = parts[parts.length - 1] || ''
    
    if (lastPart && lastPart.includes('.') && lastPart.length < url.length) {
      const extension = lastPart.split('.').pop() || ''
      const nameWithoutExt = lastPart.substring(0, lastPart.lastIndexOf('.'))
      const maxNameLength = maxLength - extension.length - 4
      
      if (nameWithoutExt.length > 0 && extension.length > 0 && extension.length < 10) {
        if (lastPart.length <= maxLength) {
          return lastPart
        }
        
        if (nameWithoutExt.length <= maxNameLength) {
          return lastPart
        }
        
        return `${nameWithoutExt.substring(0, maxNameLength)}...${extension}`
      }
    }
    
    if (url.length <= maxLength) return url
    return `${url.substring(0, maxLength - 3)}...`
  }

  const isUrl = (str: string): boolean => {
    try {
      new URL(str)
      return true
    } catch {
      const urlPattern = /^(https?:\/\/|www\.)[\w\-._~:/?#[\]@!$&'()*+,;=%]+/i
      return urlPattern.test(str)
    }
  }

  const formatUrl = (url: string): string => {
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url
    }
    if (url.startsWith('www.')) {
      return `https://${url}`
    }
    return `https://${url}`
  }

  const applyLinks = (str: string): React.ReactNode[] => {
    const urlRegex = /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+|[^\s<>"']+\.[a-z]{2,}(?:\/[^\s<>"']*)?)/gi
    const parts: React.ReactNode[] = []
    let lastIndex = 0
    let match
    
    while ((match = urlRegex.exec(str)) !== null) {
      if (match.index > lastIndex) {
        const textBefore = str.substring(lastIndex, match.index)
        parts.push(textBefore)
      }
      
      let url = match[0]
      const trailingPunctuation = /[.,;:!?]+$/.exec(url)
      let punctuation = ''
      
      if (trailingPunctuation) {
        punctuation = trailingPunctuation[0]
        url = url.slice(0, -punctuation.length)
      }
      
      if (isUrl(url)) {
        const fullUrl = formatUrl(url)
        const displayUrl = truncateUrl(url, 50)
        highlightIndex++
        parts.push(
          <a
            key={`${messageId}-url-${highlightIndex}`}
            href={fullUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.messageLink}
            title={url}
          >
            {displayUrl}
          </a>
        )
        if (punctuation) {
          parts.push(punctuation)
        }
      } else {
        parts.push(match[0])
      }
      
      lastIndex = match.index + match[0].length
    }
    
    if (lastIndex < str.length) {
      parts.push(str.substring(lastIndex))
    }
    
    return parts.length > 0 ? parts : [str]
  }

  const applyHighlights = (str: string): React.ReactNode[] => {
    const withLinks = applyLinks(str)
    
    let result: React.ReactNode[] = withLinks
    
    result = result.flatMap((part) => {
      if (typeof part === 'string') {
        return applyMarkdownBold(part)
      }
      return part
    })
    
    HIGHLIGHT_TERMS.forEach(term => {
      result = result.flatMap((part) => {
        if (typeof part !== 'string') return part
        
        const regex = new RegExp(`(${term})`, 'gi')
        const parts = part.split(regex)
        
        return parts.map((p) => {
          if (p.toLowerCase() === term.toLowerCase()) {
            highlightIndex++
            return <span key={`${messageId}-hl-${highlightIndex}`} className={styles.highlight}>{p}</span>
          }
          return p
        })
      })
    })
    
    return result
  }

  // Si tiene formato de lista, procesar por líneas
  if (hasListFormat(text)) {
    const lines = text.split('\n')
    return lines.map((line, lineIndex) => {
      const trimmedLine = line.trim()
      if (!trimmedLine) return null
      
      const isBullet = trimmedLine.startsWith('•') || trimmedLine.startsWith('-')
      const content = isBullet ? trimmedLine.substring(1).trim() : trimmedLine
      
      return (
        <span 
          key={`${messageId}-l-${lineIndex}`} 
          className={isBullet ? styles.bulletItem : styles.sentence}
        >
          {isBullet && <span className={styles.bullet}>•</span>}
          {applyHighlights(content)}
        </span>
      )
    }).filter(Boolean) as React.ReactNode[]
  }
  
  // Si no tiene lista, separar por oraciones
  const sentences = text.split(/(?<=\.)\s+(?=[A-Z¿])|(?<=\?)\s+(?=[A-Z¿])/)
  
  return sentences.map((sentence, sentenceIndex) => {
    return (
      <span key={`${messageId}-s-${sentenceIndex}`} className={styles.sentence}>
        {applyHighlights(sentence)}
      </span>
    )
  })
}

// Parsea el contenido del mensaje en modo guía (puede ser texto parcial mientras se escribe)
const parseGuideContent = (content: string) => {
  // Separar la introducción de los pasos
  const firstStepMatch = content.match(/Paso\s*1\s*:/)
  
  if (!firstStepMatch) {
    return { intro: content, steps: [], outro: '', partialStep: '' }
  }

  const introEndIndex = content.indexOf(firstStepMatch[0])
  const intro = content.substring(0, introEndIndex).trim()
  
  // Extraer los pasos de la parte restante
  const stepsSection = content.substring(introEndIndex)
  const steps: string[] = []
  let partialStep = ''
  let outro = ''
  
  // Buscar la última pregunta para separar el outro
  const lastQuestionIndex = stepsSection.lastIndexOf('¿')
  const stepsOnlySection = lastQuestionIndex > 0 
    ? stepsSection.substring(0, lastQuestionIndex)
    : stepsSection
  
  if (lastQuestionIndex > 0) {
    outro = stepsSection.substring(lastQuestionIndex).trim()
  }
  
  // Dividir por "Paso N:" considerando que puede haber texto parcial
  const stepParts = stepsOnlySection.split(/(?=Paso\s*\d+\s*:)/)
  
  // Identificar pasos completos (tienen salto de línea después del contenido o son seguidos por otro paso)
  stepParts.forEach((part, index) => {
    if (part.match(/^Paso\s*\d+\s*:/)) {
      const isLastPart = index === stepParts.length - 1
      
      // Un paso está completo si:
      // 1. No es el último (hay otro paso después)
      // 2. Tiene un salto de línea después del contenido del paso
      const stepContent = part.replace(/^Paso\s*\d+\s*:/, '').trim()
      const hasNewlineAfterContent = part.includes('\n') && stepContent.length > 0
      const isFollowedByNextStep = !isLastPart && stepParts[index + 1]?.match(/^Paso\s*\d+\s*:/)
      
      if (isFollowedByNextStep || (hasNewlineAfterContent && !isLastPart)) {
        // Paso completo
        steps.push(part.trim().replace(/\n+/g, ' '))
      } else if (isLastPart) {
        // Último paso - puede ser parcial
        partialStep = part.trim()
      }
    }
  })

  return { intro, steps, outro, partialStep }
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ 
  message, 
  suggestion,
  onSuggestionAction,
  isLatest = false,
  onTypingStateChange
}) => {
  const isUser = message.role === 'user'
  const isGuide = !isUser && isGuideMessage(message.content)
  const shouldAnimate = !isUser && isLatest
  const { displayedText, isTyping } = useTypewriter(message.content, shouldAnimate)
  
  // Rastrear el estado anterior para evitar notificaciones innecesarias
  const previousTypingState = useRef<boolean | undefined>(undefined)
  const callbackRef = useRef(onTypingStateChange)
  
  // Mantener el callback actualizado sin causar re-renders
  useEffect(() => {
    callbackRef.current = onTypingStateChange
  }, [onTypingStateChange])

  // Notificar cambios en el estado de typing solo cuando realmente cambie
  useEffect(() => {
    if (previousTypingState.current !== isTyping) {
      previousTypingState.current = isTyping
      if (callbackRef.current) {
        callbackRef.current(message.id, isTyping)
      }
    }
  }, [isTyping, message.id])

  // Renderizar mensaje con formato de guía
  if (isGuide) {
    // Siempre parsear el contenido (puede ser parcial mientras se escribe)
    const guideContent = parseGuideContent(displayedText)
    const { intro, steps, outro, partialStep } = guideContent
    
    const showActionButton = !isUser && suggestion && message.actionId === suggestion.id && onSuggestionAction && !isTyping
    
    return (
      <div
        className={`${styles.messageBubble} ${styles.assistantMessage} ${styles.guideMessage} ${isTyping ? styles.typing : ''}`}
      >
        <div className={styles.messageContent}>
          {intro && (
            <p className={styles.guideIntro}>{intro}</p>
          )}
          
          <div className={styles.guideSteps}>
            {steps.map((step, index) => {
              const stepMatch = step.match(/^Paso\s*(\d+)\s*:\s*([\s\S]+)$/)
              if (stepMatch) {
                return (
                  <div key={index} className={styles.guideStep}>
                    <span className={styles.stepNumber}>{stepMatch[1]}</span>
                    <span className={styles.stepContent}>{stepMatch[2].trim()}</span>
                  </div>
                )
              }
              return null
            })}
            
            {/* Mostrar paso parcial mientras se escribe */}
            {partialStep && (
              <div className={styles.guideStep}>
                {partialStep.match(/^Paso\s*(\d+)\s*:\s*(.*)$/) ? (
                  <>
                    <span className={styles.stepNumber}>{partialStep.match(/^Paso\s*(\d+)\s*:/)?.[1]}</span>
                    <span className={styles.stepContent}>
                      {partialStep.replace(/^Paso\s*\d+\s*:\s*/, '').replace(/\n/g, ' ').trim()}
                      {isTyping && <span className={styles.cursor}>|</span>}
                    </span>
                  </>
                ) : (
                  <span className={styles.stepContent}>
                    {partialStep.replace(/\n/g, ' ')}
                    {isTyping && <span className={styles.cursor}>|</span>}
                  </span>
                )}
              </div>
            )}
          </div>
          
          {outro && (
            <p className={styles.guideOutro}>
              {outro}
              {isTyping && <span className={styles.cursor}>|</span>}
            </p>
          )}
          
          {/* Si no hay pasos todavía y estamos escribiendo, mostrar intro parcial con cursor */}
          {!intro && steps.length === 0 && !partialStep && displayedText && (
            <p className={styles.guideIntro}>
              {displayedText}
              {isTyping && <span className={styles.cursor}>|</span>}
            </p>
          )}
          
          {/* Botón de acción claro al final del modo guía */}
          {showActionButton && (
            <button
              type="button"
              className={styles.guideActionButton}
              onClick={onSuggestionAction}
              aria-label={suggestion?.label}
            >
              Comenzar →
            </button>
          )}
        </div>
      </div>
    )
  }

  const showActionButton = !isUser && suggestion && message.actionId === suggestion.id && onSuggestionAction && !isTyping

  // Renderizar mensaje normal
  const textToShow = isUser ? message.content : displayedText

  return (
    <div
      className={`${styles.messageBubble} ${
        isUser ? styles.userMessage : styles.assistantMessage
      } ${isTyping ? styles.typing : ''}`}
    >
      <div className={styles.messageContent}>
        <div className={styles.messageText}>
          {isUser ? message.content : formatMessageText(textToShow, message.id)}
          {isTyping && <span className={styles.cursor}>|</span>}
        </div>
        
        {showActionButton && (
          <button
            type="button"
            className={styles.actionLink}
            onClick={onSuggestionAction}
            aria-label={suggestion.label}
          >
            {suggestion.label} →
          </button>
        )}
      </div>
    </div>
  )
}

export default MessageBubble

